import React from 'react';
import { Button, Icon, Input } from '@aqarmap/aqarmap-ui';
import DatePicker from 'react-datepicker';
import { useFormContext } from 'react-hook-form';
import { EvaluationSteps } from '~valuation/enums';
import 'react-datepicker/dist/react-datepicker.css';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';
import { useBreakpoint } from '~/hooks';

const PropertyPriceStep = ({
  setActiveStep,
}: {
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const { t, i18n } = useTranslation('app');
  const locale = i18n.language;
  const {
    setValue,
    watch,
    setError,
    formState: { errors },
    clearErrors,
  } = useFormContext();
  const { mobileScreen } = useBreakpoint();
  const priceValue = watch('purchasePrice') || '';
  const date = watch('purchaseDate') || null;

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    clearErrors('purchasePrice');
    const num = e.target.value.split(',').join('');
    if (num === '') {
      setValue('purchasePrice', '');
      return;
    }
    if (/^\d*$/.test(num)) {
      setValue('purchasePrice', Number(num));
    }
  };

  const handleDateChange = (dateValue: Date | null) => {
    clearErrors('purchaseDate');
    setValue(
      'purchaseDate',
      dateValue
        ? String(`${dateValue.getFullYear()}-${dateValue.getMonth() + 1}-1`)
        : '',
    );
  };

  const handleNext = async () => {
    const isValid = watch('purchasePrice') && watch('purchaseDate');
    if (isValid) setActiveStep(EvaluationSteps.USER_ESTIMATED_PRICE);
    else {
      ['purchasePrice', 'purchaseDate'].map((item) => {
        if (!watch(item)) setError(item, {});
      });
    }
  };

  return (
    <div className="flex flex-col gap-3x md:gap-5x">
      <div className="flex flex-col gap-2x px-2x md:px-0">
        <div className="flex gap-x items-center">
          <p className="text-lg text-title_4 text-black">
            {t('purchaseAddPrice')}
          </p>
          <div
            data-tip="tooltipText"
            data-tooltip-id="PropertyPriceStep"
            data-tooltip-content={t('purchasePriceTooltipText')}
            data-tooltip-place={
              mobileScreen ? 'bottom' : locale === 'ar' ? 'left' : 'right'
            }
          >
            <Icon name="info" size="16px" className="text-blue_dark_1" />
          </div>
          <Tooltip id="PropertyPriceStep" className="max-w-[272px]" />
        </div>
        <div className="flex flex-col gap-3">
          <div
            className={`${errors.purchasePrice ? 'border-red_dark' : ''} flex gap-1.5x rounded-[12px] border border-[#E0E0E0] h-[40px] px-1.5x py-x focus-within:border-blue_light_1 transition-all`}
          >
            <Input
              field={{
                type: 'text',
                placeholder: t('purchaseAddPricePlaceholder'),
                value: priceValue ? Number(priceValue).toLocaleString() : '',
                onChange: handlePriceChange,
                className:
                  'text-body_2 text-gray__dark_2 placeholder:text-gray__dark_1 flex-1 w-[60px] !px-[1px]',
              }}
              className="[&>div]:!px-0 [&>div]:!border-none [&>div]:!focus-within:!border-none &>div]:!rounded-none"
            />
            <p className="h-[24px] ps-x border-s border-gray text-body_2 text-gray__dark_2 whitespace-nowrap">
              {t('egyptianPound')}
            </p>
          </div>
          {errors.purchasePrice && (
            <p className="text-red_dark">{t('purchasePriceRequired')}</p>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-2x px-2x md:px-0">
        <p className="text-lg text-title_4 text-black flex md:gap-x items-center flex-wrap">
          <span>{t('purchaseEnterDate')}</span>
          <span className="text-link">{t('monthAndYear')}</span>
        </p>
        <div className="flex flex-col gap-3">
          <div
            className={`${errors.purchaseDate ? 'border-red_dark' : ''} flex items-center gap-1.5x rounded-[12px] border border-[#E0E0E0] h-[40px] px-1.5x focus-within:border-blue_light_1 transition-all`}
          >
            <div className="cursor-pointer">
              <Icon name="calender" className="w-6 h-6 text-gray__dark_1" />
            </div>
            <DatePicker
              selected={date}
              onChange={handleDateChange}
              dateFormat="MM/yyyy"
              showMonthYearPicker
              maxDate={new Date()}
              placeholderText="MM/YYYY"
              customInput={
                <input
                  type="text"
                  placeholder="MM/YYYY"
                  className="text-body_2 text-gray__dark_2 placeholder:text-gray__dark_1 flex-1 w-full outline-none bg-transparent"
                />
              }
            />
          </div>
        </div>
        {errors.purchaseDate && (
          <p className="text-red_dark">{t('purchaseDateRequired')}</p>
        )}{' '}
      </div>
      <div className="flex justify-center pt-3x px-2x md:px-3x border-t border-gray w-full gap-1.5x md:gap-5x">
        <Button
          variant="primary"
          size="medium"
          //disabled={!isValid}
          className="text-link w-full max-w-[271px]"
          onClick={() => handleNext()}
        >
          {t('next')}
        </Button>
        <Button
          variant="secondary"
          size="medium"
          className="text-link w-full max-w-[271px]"
          onClick={() => setActiveStep(EvaluationSteps.PROPERTY_DETAILS)}
        >
          {t('back')}{' '}
        </Button>
      </div>
    </div>
  );
};

export default PropertyPriceStep;
